import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  FileText,
  Code2,
  FolderOpen,
  CheckCircle2,
  ArrowRight,
  Lightbulb,
  Archive,
  Bot,
  Sparkles,
} from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'

interface SlideProps {
  isActive: boolean
}

const workflowPhases = [
  {
    id: 'research',
    icon: Search,
    label: 'Research',
    shortDesc: 'Investigate & gather context',
    color: 'from-electric-cyan to-blue-500',
    bgColor: 'bg-electric-cyan/10',
    textColor: 'text-electric-cyan',
    borderColor: 'border-electric-cyan',
    storage: '.claude/research/<task>/',
    tasks: [
      'Investigate codebase structure',
      'Gather requirements & context',
      'Identify edge cases upfront',
      'Review existing solutions',
    ],
    output: 'Research findings in markdown',
    tip: 'Store findings in separate .md files by topic',
  },
  {
    id: 'plan',
    icon: FileText,
    label: 'Plan',
    shortDesc: 'Design implementation strategy',
    color: 'from-electric-purple to-purple-600',
    bgColor: 'bg-electric-purple/10',
    textColor: 'text-electric-purple',
    borderColor: 'border-electric-purple',
    storage: '.claude/plans/<task>.md',
    tasks: [
      'Break down into phases & steps',
      'Define file modifications',
      'Include code examples',
      'ONE plan per task',
    ],
    output: 'Single actionable plan file',
    tip: 'Plan is for AI reference during implementation',
  },
  {
    id: 'implement',
    icon: Code2,
    label: 'Implement',
    shortDesc: 'Execute plan step by step',
    color: 'from-aurora-teal to-teal-600',
    bgColor: 'bg-aurora-teal/10',
    textColor: 'text-aurora-teal',
    borderColor: 'border-aurora-teal',
    storage: 'Codebase changes',
    tasks: [
      'Follow plan sequentially',
      'One task in_progress at a time',
      'Mark completed immediately',
      'Validate before moving on',
    ],
    output: 'Working, tested code',
    tip: 'Clear context between major phases',
  },
]

const storageStructure = [
  { path: '.claude/', icon: Bot, desc: 'AI workspace root' },
  { path: '  research/<task>/', icon: Search, desc: 'Investigation findings' },
  { path: '  plans/<task>.md', icon: FileText, desc: 'ONE plan per task' },
  { path: '  archive/', icon: Archive, desc: 'Completed work' },
]

const bestPractices = [
  'Don\'t jump straight to coding — research first',
  'Test-driven development (TDD) is powerful with AI',
  'Clear context between phases improves output',
  'Keep plans technical, not project management',
]

export default function WorkflowOverviewSlide({ isActive: _isActive }: SlideProps) {
  const [activePhase, setActivePhase] = useState(0)

  return (
    <Slide>
      <StaggerContainer className="h-full flex flex-col">
        {/* Header */}
        <StaggerItem>
          <div className="mb-6 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-2">
              AI Coding <span className="text-gradient">Workflow</span>
            </h2>
            <p className="text-base text-white/60 mb-8">
              The structured approach for complex tasks
            </p>
          </div>
        </StaggerItem>

        {/* Large Phase Cards - Horizontal Flow */}
        <StaggerItem>
          <div className="flex items-stretch justify-center gap-4 mb-8">
            {workflowPhases.map((phase, index) => {
              const Icon = phase.icon
              const isActive = activePhase === index
              return (
                <div key={phase.id} className="flex items-center">
                  <motion.button
                    onClick={() => setActivePhase(index)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + index * 0.15 }}
                    whileHover={{ scale: 1.05, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative flex flex-col items-center gap-3 px-8 py-6 rounded-2xl transition-all duration-300 min-w-[160px] ${
                      isActive
                        ? `bg-gradient-to-br ${phase.color} text-white shadow-2xl ring-4 ring-white/20`
                        : 'glass text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {/* Step number badge */}
                    <motion.div
                      className={`absolute -top-3 -left-3 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        isActive
                          ? 'bg-white text-space-900'
                          : 'bg-white/20 text-white/60'
                      }`}
                      animate={isActive ? { scale: [1, 1.1, 1] } : {}}
                      transition={{ duration: 0.5, repeat: isActive ? Infinity : 0, repeatDelay: 2 }}
                    >
                      {index + 1}
                    </motion.div>

                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      isActive ? 'bg-white/20' : 'bg-white/10'
                    }`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <span className="text-xl font-bold">{phase.label}</span>
                    <span className={`text-sm ${isActive ? 'text-white/80' : 'text-white/50'}`}>
                      {phase.shortDesc}
                    </span>

                    {/* Active indicator */}
                    {isActive && (
                      <motion.div
                        className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-white"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                      />
                    )}
                  </motion.button>

                  {/* Arrow connector */}
                  {index < workflowPhases.length - 1 && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 + index * 0.15 }}
                      className="mx-4"
                    >
                      <motion.div
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <ArrowRight className="w-8 h-8 text-white/30" />
                      </motion.div>
                    </motion.div>
                  )}
                </div>
              )
            })}
          </div>
        </StaggerItem>

        {/* Main content grid */}
        <StaggerItem className="flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
            {/* Active phase details */}
            <div className="lg:col-span-2">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activePhase}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  className={`glass rounded-2xl p-6 h-full flex flex-col border-l-4 ${workflowPhases[activePhase].borderColor}`}
                >
                  {(() => {
                    const phase = workflowPhases[activePhase]
                    const Icon = phase.icon
                    return (
                      <>
                        <div className="flex items-start gap-4 mb-4">
                          <motion.div
                            className={`w-16 h-16 rounded-xl bg-gradient-to-br ${phase.color} flex items-center justify-center flex-shrink-0`}
                            animate={{ rotate: [0, 5, -5, 0] }}
                            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                          >
                            <Icon className="w-8 h-8 text-white" />
                          </motion.div>
                          <div>
                            <h3 className="text-2xl font-bold text-white mb-1">
                              {phase.label} Phase
                            </h3>
                            <p className="text-lg text-white/60">{phase.shortDesc}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
                          {/* Tasks */}
                          <div>
                            <h4 className="text-sm text-white/40 uppercase tracking-wider mb-3 flex items-center gap-2">
                              <Sparkles className="w-4 h-4" />
                              Key Activities
                            </h4>
                            <ul className="space-y-2">
                              {phase.tasks.map((task, i) => (
                                <motion.li
                                  key={task}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: i * 0.1 }}
                                  className="flex items-start gap-2"
                                >
                                  <CheckCircle2 className={`w-5 h-5 ${phase.textColor} flex-shrink-0 mt-0.5`} />
                                  <span className="text-white/70">{task}</span>
                                </motion.li>
                              ))}
                            </ul>
                          </div>

                          {/* Output & Storage */}
                          <div className="flex flex-col gap-4">
                            <motion.div
                              className={`${phase.bgColor} rounded-xl p-4 border border-white/10`}
                              whileHover={{ scale: 1.02 }}
                            >
                              <h4 className="text-sm text-white/40 uppercase tracking-wider mb-2">
                                Storage Location
                              </h4>
                              <code className={`text-lg font-mono ${phase.textColor}`}>
                                {phase.storage}
                              </code>
                            </motion.div>
                            <div className="glass rounded-xl p-4">
                              <h4 className="text-sm text-white/40 uppercase tracking-wider mb-2">
                                Output
                              </h4>
                              <p className="text-white/70">{phase.output}</p>
                            </div>
                          </div>
                        </div>

                        {/* Tip */}
                        <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-electric-purple/10 border border-electric-purple/20">
                          <Lightbulb className="w-4 h-4 text-electric-purple flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-white/60">{phase.tip}</p>
                        </div>
                      </>
                    )
                  })()}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Right sidebar */}
            <div className="flex flex-col gap-4">
              {/* Storage structure */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="glass rounded-2xl p-4"
              >
                <h4 className="text-sm text-white/40 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" />
                  Project Structure
                </h4>
                <div className="font-mono text-sm space-y-1">
                  {storageStructure.map((item, i) => {
                    const Icon = item.icon
                    return (
                      <motion.div
                        key={item.path}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + i * 0.1 }}
                        className="flex items-center gap-2 py-1"
                      >
                        <Icon className="w-4 h-4 text-electric-cyan" />
                        <span className="text-electric-cyan">{item.path}</span>
                        <span className="text-white/40 text-xs ml-auto">{item.desc}</span>
                      </motion.div>
                    )
                  })}
                </div>
              </motion.div>

              {/* Best practices */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="glass rounded-2xl p-4 flex-1"
              >
                <h4 className="text-sm text-white/40 uppercase tracking-wider mb-3">
                  Best Practices
                </h4>
                <ul className="space-y-2">
                  {bestPractices.map((practice, i) => (
                    <motion.li
                      key={practice}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6 + i * 0.1 }}
                      className="flex items-start gap-2 text-sm"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-electric-purple mt-2 flex-shrink-0" />
                      <span className="text-white/60">{practice}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </StaggerItem>

        {/* Footer */}
        <StaggerItem>
          <div className="mt-6 mb-[100px]">
            <p className="text-xs text-white/40 text-center">
              Sources: Anthropic Best Practices • Claude Code Framework • Research → Plan → Implement Standard
            </p>
          </div>
        </StaggerItem>
      </StaggerContainer>
    </Slide>
  )
}
